import { Button, Form, InputNumber, Radio, notification } from 'antd';
import { StyledForm } from './style';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useUpdateProfessionalClientFemaleBio } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientFemaleBio } from '../../../hooks/client/useClientGeneralInfo';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { CheckCircleOutlined } from '@ant-design/icons';
import { NotificationTitle } from '../../Common/Notification/style';
import { theme } from '../../../styles/theme';
import { useEffect, useMemo } from 'react';

const FirstMenstruationCycleForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && "professionalCode" in user;

  const { update: updateProfClientFemaleBio, loading: updateProfLoading } = useUpdateProfessionalClientFemaleBio();
  const { update: updateClientFemaleBio, loading: updateClientLoading } = useUpdateClientFemaleBio();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);

  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    return {
      firstMensAge: selectedClient?.bio?.clientBioFemale?.firstMensAge,
      isInMenopause: selectedClient?.bio?.clientBioFemale?.isInMenopause,
    };
  }, [selectedClient]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const onFinish = async (values: any) => {
    try {
      const payload = {
        clientBioFemale: {
          id: selectedClient?.bio?.clientBioFemale?.id,
          ...values,
        },
      };

      const res = isClient
        ? await updateClientFemaleBio(payload)
        : await updateProfClientFemaleBio(selectedClient?.id as string, payload);

      if (res?.professionalClientUpdateFemaleBio || res?.clientUpdateFemaleBio) {
        api.info({
          message: <NotificationTitle>First Menstruation Cycle updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon: <CheckCircleOutlined style={{ color: theme.color.primary }} />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex',
          },
        });
        await refetchClient();
      }
    } catch (error) {
      api.error({
        message: <NotificationTitle>Unable to update First Menstruation Cycle</NotificationTitle>,
        placement: 'topRight',
        style: {
          background: `${theme.bg.secondary}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex',
        },
      });
      console.error('Error in FirstMenstruationCycleForm onFinish: ', error);
    }
  };

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="first_menstruation_cycle_form"
      onFinish={onFinish}
      initialValues={initialValues}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <Form.Item
        name="firstMensAge"
        label="First menstruation age"
        labelAlign="left"
        rules={[
          { required: true, message: 'Please enter first menstruation age' }
        ]}
      >
        <InputNumber min={0} />
      </Form.Item>
      <Form.Item
        name="isInMenopause"
        label="In menopause?"
        labelAlign="left"
        rules={[
          { required: true, message: 'Please select menopause status' }
        ]}
      >
        <Radio.Group>
          <Radio value={true}> Yes </Radio>
          <Radio value={false}> No </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit" loading={isClient ? updateClientLoading : updateProfLoading}>
          Save
        </Button>
      </Form.Item>
    </StyledForm>
  );
};

export default FirstMenstruationCycleForm;
